import { ChangeDetectionStrategy, Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { UserModel } from '../../../user.model';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import {
    FiUserEditDataFieldEmailAddress_Settings,
    TFiUserEditDataFieldEmailAddress_Settings,
} from '@fi-app/data-field/data-field-edit/email-address/email-address.component';
import {
    FiUserEditDataFieldPhoneNumber_Settings,
    TFiUserEditDataFieldPhoneNumber_Settings,
} from '@fi-app/data-field/data-field-edit/phone-number/phone-number.component';
import { FiUserEditDataFieldAddress_Settings } from '@fi-app/data-field/data-field-edit/address/address.component';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { IUserContactUniqueData } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { FiUserHelperService } from '@fi-shared/fi-user-shared/fi-user-helper.service';
import { IContactModel } from '@fi-shared/models/common';
import { FiUserSignInSettings } from '@fi-app/user/user-edit/fi-user-sign-in-settings';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { IFiUniqueContactData } from '@fi-shared/fi-directives/fi-custom-validators/fi-unique-contact-async-validator.directive';
import { FiUserContactDataItemType } from '@fi-shared/fi-contacts/fi-contacts.service';
import { IFiContactData } from '@fi-shared/fi-contacts/fi-contacts';
import { Subject } from 'rxjs';

@Component({
    selector: 'fi-user-edit-contact-tab',
    templateUrl: './fi-user-edit-contact-tab.component.html',
    styleUrls: ['./fi-user-edit-contact-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-edit-contact-tab' },
    providers: [
        {
            provide: FiUserEditDataFieldEmailAddress_Settings,
            deps: [FiUserEditContactTabComponent],
            useFactory: (
                userEditComponent: FiUserEditContactTabComponent,
            ): TFiUserEditDataFieldEmailAddress_Settings => {
                return {
                    isNewAddButtonLayout: true,
                    fiUserSignInSettings: userEditComponent.fiUserSignInSettings,
                    dataForUniqueEmailCheckFn: userEditComponent.getDataForUniqueEmailCheckFn(),
                    contactData: userEditComponent.getUserContactData(FiUserContactDataItemType.Emails),
                };
            },
        },
        {
            provide: FiUserEditDataFieldPhoneNumber_Settings,
            deps: [FiUserEditContactTabComponent],
            useFactory: (
                userEditComponent: FiUserEditContactTabComponent,
            ): TFiUserEditDataFieldPhoneNumber_Settings => {
                return {
                    isCountryCodeNewLayout: true,
                    isNewAddButtonLayout: true,
                    fiUserSignInSettings: userEditComponent.fiUserSignInSettings,
                    contactData: userEditComponent.getUserContactData(FiUserContactDataItemType.PhoneNumbers),
                };
            },
        },
        {
            provide: FiUserEditDataFieldAddress_Settings,
            useFactory: () => {
                return {
                    isNewAddButtonLayout: true,
                };
            },
        },
        { provide: FiComponentsContainer, useExisting: FiUserEditContactTabComponent },
    ],
    standalone: false,
})
export class FiUserEditContactTabComponent extends FiComponentsContainer implements OnDestroy {
    @Input() user: UserModel;
    @Input() dataFieldsEmail: DataFieldModel[] = [];
    @Input() dataFieldsPhoneNumber: DataFieldModel[] = [];
    @Input() dataFieldsAddress: DataFieldModel[] = [];
    @Input() userEmailUniqueData: IUserContactUniqueData[];
    @Input() fiUserSignInSettings: FiUserSignInSettings;

    constructor(
        private detectDevicesService: DetectDevicesService,
        protected userHelperService: FiUserHelperService,
        private contactService: ContactService,
    ) {
        super();
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    isPKUser() {
        return this.userHelperService.isPKUser(this.user);
    }

    getDataForUniqueEmailCheckFn(): (contact: IContactModel) => IFiUniqueContactData {
        return this.userHelperService.getDataForUniqueEmailCheckFn(
            this.user.Id,
            this.user.IsSystemObject,
            this.user.EmailAddressContacts,
        );
    }

    getUserContactData(userContactDataItemType: FiUserContactDataItemType): IFiContactData {
        return this.userHelperService.getUserContactData({
            user: this.user,
            userContactDataItemType: userContactDataItemType,
            contactUniqueDataFn: () => this.userEmailUniqueData,
            isPKUser: this.isPKUser(),
            onDestroy$: this.ngOnDestroy$.asObservable(),
        });
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
